import { AgendaSlotType, UnauthorizedError } from '@omecare/utils';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import http from '@/utils/http.service';
import { DataTableSearch } from '../../components/data-table/use-data-table';

const getDoctorSubstituteItemSchema = z.object({
  id: z.string(),
  firstname: z.string(),
  lastname: z.string(),
  email: z.string(),
  orderRegistrationNumber: z.string(),
  createdAt: z.coerce.date(),
  isAvailable: z.boolean().optional(),
  priority: z.number().nullable().optional(),
  installable: z.boolean().nullable().optional(),
});
export type GetDoctorSubstituteItemResponse = z.infer<
  typeof getDoctorSubstituteItemSchema
>;

const getDoctorSubstitutesResponseSchema = z.object({
  page: z.number(),
  per_page: z.number(),
  page_count: z.number(),
  data: z.array(getDoctorSubstituteItemSchema),
});

export type GetDoctorSubstitutesResponse = z.infer<
  typeof getDoctorSubstitutesResponseSchema
>;

export type GetDoctorSubstitutesPayload = DataTableSearch & {
  officeId?: string;
  officeIds?: string[];
  officeAccessType?: AgendaSlotType;
  filter?: string;
  date?: string;
  period?: string;
  includeDeleted?: boolean;
};
const getDoctorSubstitutes =
  (params: GetDoctorSubstitutesPayload) => async () => {
    const queryParams = {
      ...params,
      officeIds: params.officeIds?.join(','),
    };
    return http.doctorSubstitutes
      .getList(queryParams)
      .then<GetDoctorSubstitutesResponse>(({ response, transform }) => {
        return transform.handle({
          response,
          attributes: {
            firstname: true,
            lastname: true,
            email: true,
            orderRegistrationNumber: true,
            createdAt: true,
            isAvailable: true,
            priority: true,
            installable: true,
          },
          relations: {
            siteAccesses: true,
          },
          schema: getDoctorSubstitutesResponseSchema,
        });
      });
  };

export const useGetDoctorSubstitutesQuery = (
  params: GetDoctorSubstitutesPayload,
): UseQueryResult<GetDoctorSubstitutesResponse, UnauthorizedError> => {
  return useQuery({
    queryFn: getDoctorSubstitutes(params),
    queryKey: [
      'doctor-substitutes',
      params.page,
      params.per_page,
      params.filter,
      params.officeId,
      params.officeIds,
      params.officeAccessType,
      params.date,
      params.period,
      params.includeDeleted,
    ],
    retry: false,
  });
};
